import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Site-wide feature flags fetched once from /api/config/site at app load.
 * Drives:
 *   - premiumGatingEnabled: whether L4+ requires a paid subscription
 *   - adsEnabled: whether AdSlot components render their "your ad here"
 *     placeholders (later: real AdSense / AdMob tags)
 *   - freeMaxLevel: highest level the free tier can play (mirrors backend
 *     SUBSCRIPTION_TIERS["free"]["max_level"] so the UI stays in sync)
 *
 * Defaults to "adoption mode" (gating OFF, ads OFF) so the UI is permissive
 * if the network call fails — better to over-grant than to lock out users
 * during a transient hiccup.
 */
const DEFAULTS = {
  premiumGatingEnabled: false,
  adsEnabled: false,
  freeMaxLevel: 17,
  foundingPromoActive: false,
  foundingMemberCount: 0,
  loaded: false,
};

const SiteConfigContext = createContext(DEFAULTS);

export function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULTS);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;
    axios
      .get(`${API}/api/config/site`)
      .then((r) => {
        if (cancelled) return;
        setConfig({
          premiumGatingEnabled: Boolean(r.data?.premium_gating_enabled),
          adsEnabled: Boolean(r.data?.ads_enabled),
          freeMaxLevel: Number(r.data?.free_max_level ?? 17),
          foundingPromoActive: Boolean(r.data?.founding_promo_active),
          foundingMemberCount: Number(r.data?.founding_member_count ?? 0),
          loaded: true,
        });
      })
      .catch(() => {
        // Permissive defaults already in state; just mark as loaded so
        // children stop spinning.
        if (!cancelled) setConfig((c) => ({ ...c, loaded: true }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SiteConfigContext.Provider value={config}>
      {children}
    </SiteConfigContext.Provider>
  );
}

export function useSiteConfig() {
  return useContext(SiteConfigContext);
}
